import React, { useState } from 'react';

// Styles & Assets
import styles from './FormQuestions.module.css';
import { ReactComponent as AddQuestionSVG } from '../../../assets/icons/add-circle.svg';
import { ReactComponent as ReorderSVG } from '../../../assets/icons/reorder.svg';

// Services
import { sortByAttribute } from '../../../services/sort';

// Components
import QuestionPoll from '../QuestionPoll/QuestionPoll';

// Language
import { useTranslation } from 'react-i18next';

// Interfaces
import { PollQuestion } from '../../../interfaces/Poll';
import { PollDispatch } from '../../../interfaces/Reducers';

// Other
import { DropResult } from 'react-beautiful-dnd';
import DraggableList from '../../UI/DraggableList/DraggableList';
import TitlePollForm from '../TitlePollForm/TitlePollForm';
import AddQuestionDialog from '../AddQuestionDialog/AddQuestionDialog';
import Modal from '../../UI/Modal/Modal';
import ButtonForm from '../ButtonForm/ButtonForm';
import ButtonQuestionPoll from '../ButtonQuestionPoll/ButtonQuestionPoll';

interface Props {
    questions: PollQuestion[]
    hasTable: boolean
    dispatchPoll: React.Dispatch<PollDispatch>
    error: boolean
}

const FormQuestions: React.FC<Props> = ({ error, questions, hasTable, dispatchPoll }) => {

    const { t } = useTranslation();

    const addQuestion = (question: PollQuestion) => dispatchPoll({ type: 'ADD_QUESTION', payload: question });
    const editQuestion = (index: number) => (question: PollQuestion) => dispatchPoll({ type: 'EDIT_QUESTION', payload: { question, index } });
    const deleteQuestion = (index: number) => () => dispatchPoll({ type: 'DELETE_QUESTION', payload: index });

    const [showQuestionDialog, setShowQuestionDialog] = useState(false);

    const handleDragEnd = (result: DropResult) => {
        const { source, destination } = result;
        if (destination?.index === undefined || source.index === destination.index) return;
        dispatchPoll({ type: 'SWAP_QUESTIONS_ORDER', payload: { indexA: source.index, indexB: destination.index } });
    };


    return (
        <div className={styles.container}>
            <div className={styles.questionTopBar}>
                <TitlePollForm style={{ marginBottom: 0 }}>
                    {t('questions')}
                </TitlePollForm>
                <ButtonForm onClick={() => setShowQuestionDialog(true)}>
                    {t('add_question')}
                    <AddQuestionSVG />
                </ButtonForm>
            </div>
            <div className={styles.questionsContainer}>
                {questions.length === 0 ?
                    <p className={styles.noQuestionsText}>
                        {'No se han añadido preguntas a la encuesta'}
                    </p>
                    :
                    <DraggableList
                        onDragEnd={handleDragEnd}
                        droppableId='questions'
                        orderedList={sortByAttribute(questions, 'order')}
                    >
                        {(item, index, dragHandleProps) =>
                            <QuestionPoll
                                hasTable={hasTable}
                                question={item}
                                deleteQuestion={deleteQuestion(index)}
                                editQuestion={editQuestion(index)}
                            >
                                <ButtonQuestionPoll {...dragHandleProps}>
                                    <ReorderSVG
                                        className={`${styles.icon} ${styles.iconDrag}`}
                                    />
                                </ButtonQuestionPoll>
                            </QuestionPoll>
                        }
                    </DraggableList>
                }
            </div>
            {
                error &&
                <p className='errorMsg'>
                    {t('must_add_questions')}
                </p>
            }
            <Modal show={showQuestionDialog} setShowModal={setShowQuestionDialog}>
                <AddQuestionDialog
                    order={questions.length}
                    hasTable={hasTable}
                    addQuestion={addQuestion}
                    setShowQuestionDialog={setShowQuestionDialog}
                />
            </Modal>
        </div >
    );
};

export default FormQuestions;